import type { ReactNode } from 'react';

/**
 * The typographic kit the four public documents share.
 *
 * These pages are read by two audiences with nothing in common — a person
 * looking for the sentence that says what happens to their messages, and a
 * platform reviewer checking that a required page exists and is specific. Both
 * are served by the same thing: numbered clauses, short paragraphs, and a term
 * list wherever a claim needs a value next to it.
 *
 * The composition is the landing page's: one plate on the paper ground,
 * hairlines instead of shadows, serif for display type only.
 */

export function Clause({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="mt-12 first:mt-0 scroll-mt-24" id={`clause-${n}`}>
      <div className="flex items-baseline gap-3">
        <span className="text-[11px] uppercase tracking-[0.2em] text-ink/40 font-medium tabular">
          {String(n).padStart(2, '0')}
        </span>
        <h2 className="font-serif text-xl sm:text-2xl font-normal leading-tight tracking-tight text-ink">
          {title}
        </h2>
      </div>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

export function P({ children }: { children: ReactNode }) {
  return <p className="text-sm md:text-[15px] text-ink/70 leading-relaxed">{children}</p>;
}

/** A short, emphatic line. Used where a document must not sound hedged. */
export function Plain({ children }: { children: ReactNode }) {
  return <p className="text-sm md:text-[15px] text-ink leading-relaxed">{children}</p>;
}

export function List({ items }: { items: ReactNode[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item, index) => (
        <li key={index} className="flex gap-3 text-sm md:text-[15px] text-ink/70 leading-relaxed">
          <span aria-hidden="true" className="mt-[0.55rem] h-px w-3 shrink-0 bg-hairline" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function Steps({ items }: { items: ReactNode[] }) {
  return (
    <ol className="space-y-3">
      {items.map((item, index) => (
        <li key={index} className="flex gap-4 text-sm md:text-[15px] text-ink/70 leading-relaxed">
          <span className="mt-[0.15rem] text-[11px] tabular text-ink/40 font-medium w-4 shrink-0">
            {index + 1}
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ol>
  );
}

export interface Term {
  term: ReactNode;
  detail: ReactNode;
}

/** Definition list on a hairline grid — the shape used for retention and sub-processors. */
export function Terms({ items }: { items: Term[] }) {
  return (
    <dl className="divide-y divide-hairline border-y border-hairline">
      {items.map((item, index) => (
        <div key={index} className="grid sm:grid-cols-[minmax(0,13rem)_1fr] gap-1 sm:gap-6 py-4">
          <dt className="text-sm font-medium text-ink">{item.term}</dt>
          <dd className="text-sm text-ink/70 leading-relaxed">{item.detail}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Inline emphasis for a value, an address or a code. */
export function Mono({ children }: { children: ReactNode }) {
  return <span className="font-mono text-[0.9em] text-ink">{children}</span>;
}
